/**
 * NOVEDADES — "qué hay de nuevo en Softfi".
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  CÓMO SE ESCRIBE UNA NOVEDAD
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Esto lo lee el dueño de una bodega entre cliente y cliente, no un programador.
 * Tres reglas:
 *
 *   1. **Se cuenta lo que PUEDE HACER, no lo que cambió por dentro.**
 *      ❌ "Se añadió el módulo de reportes con agregación por período"
 *      ✅ "Ya puedes ver cuánto ganaste de verdad, no solo cuánto vendiste"
 *
 *   2. **Nada de palabras de programador.** Ni módulo, ni endpoint, ni
 *      migración, ni refactor. Si hay que explicar la palabra, es la palabra
 *      equivocada.
 *
 *   3. **Si no le cambia el día a nadie, no va aquí.** Arreglar un error interno
 *      que nadie notó no es una novedad: es mantenimiento.
 *
 * Para añadir una: se agrega arriba del todo con la fecha de hoy. El frontend
 * recuerda cuál fue la última que leyó cada persona, así que solo se le marcan
 * como nuevas las que no ha visto.
 */

export type Novedad = {
  /** Identificador estable. Nunca se reutiliza ni se cambia. */
  id: string;
  fecha: string;
  titulo: string;
  /** Una frase por cambio. Frases cortas, en segunda persona. */
  puntos: string[];
};

/** ⚠️ Lo más nuevo va PRIMERO. */
export const NOVEDADES: Novedad[] = [
  {
    id: "2026-08-05-reportes-y-fiado",
    fecha: "2026-08-05",
    titulo: "Reportes, ventas a crédito y una cara nueva",
    puntos: [
      "Nueva pestaña **Reportes**: ya no solo ves cuánto vendiste, sino cuánto ganaste de verdad — restando lo que te costó cada producto.",
      "Si vendes algo sin costo cargado, Softfi te avisa de que la ganancia que ves está inflada. Preferimos decírtelo a darte un número bonito.",
      "**Ventas a crédito** (opcional): véndele anotado a tus clientes de siempre y lleva el control de quién te debe, desde cuándo, con abonos parciales. Se enciende en Ajustes.",
      "La lista de quien te debe va ordenada **por antigüedad**, no por monto: primero hay que llamar a quien lleva más tiempo debiendo. Cada cliente trae su botón de WhatsApp con el mensaje ya escrito.",
      "Softfi cambió de cara: colores nuevos, todo más claro y los números alineados para que una lista de precios se lea de un vistazo.",
      "En la pantalla de Inicio ahora ves cómo va el día y si vendiste más o menos que ayer.",
    ],
  },
  {
    id: "2026-08-05-impresora-y-recorrido",
    fecha: "2026-08-05",
    titulo: "Impresora térmica y recorrido guiado",
    puntos: [
      "Puedes conectar tu **impresora térmica** por cable o por Bluetooth: el recibo sale al instante, sin diálogos y con el papel cortado.",
      "Si quieres, el recibo sale **solo** al cobrar, sin que nadie toque nada.",
      "¿Nunca has usado Softfi? En Inicio tienes un **recorrido guiado** de un minuto. También sirve para enseñarle el sistema a un empleado nuevo — está en Ajustes.",
      "Si olvidas tu contraseña, ya puedes recuperarla tú mismo desde la pantalla de entrar.",
    ],
  },
];

/** La más reciente. Se usa para saber si hay algo sin leer. */
export const ULTIMA_NOVEDAD = NOVEDADES[0]?.id ?? "";
